import { useState, useEffect, useCallback, useRef } from 'react';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Alert from '@mui/material/Alert';
import Pagination from '@mui/material/Pagination';
import Skeleton from '@mui/material/Skeleton';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import InputAdornment from '@mui/material/InputAdornment';
import SearchIcon from '@mui/icons-material/Search';
import FavoriteIcon from '@mui/icons-material/Favorite';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import { Badge } from '@tinyrewards/shared';
import { getBadges, getFavourites, getAssignedBadges, addFavourite, removeFavourite } from '../api/badges';
import BadgeCard from '../components/BadgeCard';
import { BADGE_TYPES, type BadgeType } from '../constants/badgeTypes';

type SortField = 'createddate' | 'verified' | 'name';
type SortOrder = 'asc' | 'desc';
type TypeFilter = 'all' | BadgeType;

const DEFAULT_SORT: SortField  = 'createddate';
const DEFAULT_ORDER: SortOrder = 'desc';
const DEFAULT_TYPE: TypeFilter = 'all';

const PAGE_SIZE = 12;
const DEBOUNCE_MS = 400;

function SkeletonCard() {
    return (
        <Card sx={{ height: '100%' }}>
            <Skeleton variant="rectangular" height={160} />
            <CardContent>
                <Skeleton width="70%" height={28} />
                <Skeleton width="90%" />
                <Skeleton width="60%" />
            </CardContent>
        </Card>
    );
}

export default function AvailableBadgesPage() {
    const [badges, setBadges] = useState<Badge[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const [sort, setSort]           = useState<SortField>(DEFAULT_SORT);
    const [order, setOrder]         = useState<SortOrder>(DEFAULT_ORDER);
    const [typeFilter, setTypeFilter] = useState<TypeFilter>(DEFAULT_TYPE);

    const [favs, setFavs] = useState<Badge[]>([]);
    const [favIDs, setFavIDs] = useState<Set<string>>(new Set());
    const [favsLoading, setFavsLoading] = useState(true);

    const [assigned, setAssigned] = useState<Badge[]>([]);
    const [assignedLoading, setAssignedLoading] = useState(true);

    const handleSearchChange = (value: string) => {
        setSearch(value);
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            setDebouncedSearch(value);
            setPage(1);
        }, DEBOUNCE_MS);
    };

    const loadAssigned = useCallback(() => {
        setAssignedLoading(true);
        getAssignedBadges()
            .then(res => {
                if (res.success && res.data) {
                    setAssigned(res.data);
                }
            })
            .finally(() => setAssignedLoading(false));
    }, []);

    const loadFavourites = useCallback(() => {
        setFavsLoading(true);
        getFavourites()
            .then(res => {
                if (res.success && res.data) {
                    setFavs(res.data);
                    setFavIDs(new Set(res.data.map(b => b.badgeID)));
                }
            })
            .finally(() => setFavsLoading(false));
    }, []);

    const load = useCallback((p: number, q: string, s: SortField, o: SortOrder, t: TypeFilter) => {
        setLoading(true);
        setError(null);
        getBadges({
            public: true,
            page: p,
            pageSize: PAGE_SIZE,
            search: q || undefined,
            sort: s,
            order: o,
            badgeType: t === 'all' ? undefined : t,
        })
            .then((res) => {
                if (res.success) {
                    setBadges(res.data ?? []);
                    setTotal(res.total ?? 0);
                } else {
                    setError(res.message ?? 'Failed to load badges.');
                }
            })
            .catch(() => setError('An unexpected error occurred.'))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => { loadAssigned(); }, [loadAssigned]);
    useEffect(() => { loadFavourites(); }, [loadFavourites]);
    useEffect(() => { load(page, debouncedSearch, sort, order, typeFilter); }, [page, debouncedSearch, sort, order, typeFilter, load]);

    // Any control change resets to page 1 so the user sees the start of the new result set.
    const handleSortChange = (next: SortField) => { setSort(next); setPage(1); };
    const handleOrderChange = (next: SortOrder) => { setOrder(next); setPage(1); };
    const handleTypeChange = (next: TypeFilter) => { setTypeFilter(next); setPage(1); };

    const handleClear = () => {
        setSort(DEFAULT_SORT);
        setOrder(DEFAULT_ORDER);
        setTypeFilter(DEFAULT_TYPE);
        setSearch('');
        setDebouncedSearch('');
        setPage(1);
        if (debounceRef.current) clearTimeout(debounceRef.current);
    };

    const handleToggleFavourite = async (badge: Badge) => {
        const isFav = favIDs.has(badge.badgeID);
        // Optimistic update
        if (isFav) {
            setFavIDs(prev => { const next = new Set(prev); next.delete(badge.badgeID); return next; });
            setFavs(prev => prev.filter(b => b.badgeID !== badge.badgeID));
        } else {
            setFavIDs(prev => new Set(prev).add(badge.badgeID));
            setFavs(prev => [badge, ...prev]);
        }

        try {
            const res = isFav
                ? await removeFavourite(badge.badgeID)
                : await addFavourite(badge.badgeID);
            if (!res.success) {
                // Revert on failure
                loadFavourites();
            }
        } catch {
            loadFavourites();
        }
    };

    const totalPages = Math.ceil(total / PAGE_SIZE);

    // /api/badges/assigned is every OpenBadgesIssued row for this user — the same
    // predicate POST /api/badges/:id/earn uses to answer `alreadyEarned`, so it is
    // exactly the set of badges they have earned. No extra request needed for this.
    const earnedIDs = new Set(assigned.map(b => b.badgeID));

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            {/* Header */}
            <Box mb={3}>
                <Typography variant="h4" fontWeight={700}>Available Badges</Typography>
                <Typography variant="body2" color="text.secondary">Browse public badges you can earn or share.</Typography>
            </Box>

            {/* Favourites Panel */}
            {/* Waits on assignedLoading as well: nothing that shows a Go/Earn/Watch
                button should render before we know which badges are already earned,
                or the button flashes up on a card that should never offer one. */}
            {!favsLoading && !assignedLoading && favs.length > 0 && (
                <Box mb={4}>
                    <Box display="flex" alignItems="center" gap={1} mb={2}>
                        <FavoriteIcon sx={{ color: 'accent.main' }} />
                        <Typography variant="h6" fontWeight={600}>
                            My Favourites
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            ({favs.length})
                        </Typography>
                    </Box>
                    <Grid container spacing={3}>
                        {favs.map(badge => (
                            <Grid item xs={12} sm={6} md={4} key={badge.badgeID}>
                                <BadgeCard
                                    badge={badge}
                                    showGoButton
                                    isEarned={earnedIDs.has(badge.badgeID)}
                                    isFavourite
                                    onToggleFavourite={handleToggleFavourite}
                                />
                            </Grid>
                        ))}
                    </Grid>
                </Box>
            )}

            {/* Assigned Badges Panel */}
            {!assignedLoading && assigned.length > 0 && (
                <Box mb={4}>
                    <Box display="flex" alignItems="center" gap={1} mb={2}>
                        <AssignmentTurnedInIcon sx={{ color: 'accent.main' }} />
                        <Typography variant="h6" fontWeight={600}>
                            My Assigned Badges
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            ({assigned.length})
                        </Typography>
                    </Box>
                    <Grid container spacing={3} data-testid="assigned-badges-grid">
                        {assigned.map(badge => (
                            <Grid item xs={12} sm={6} md={4} key={badge.badgeID}>
                                <BadgeCard
                                    badge={badge}
                                    showGoButton
                                    isEarned  /* every badge in this panel is one they hold */
                                    isFavourite={favIDs.has(badge.badgeID)}
                                    onToggleFavourite={handleToggleFavourite}
                                />
                            </Grid>
                        ))}
                    </Grid>
                </Box>
            )}

            {/* All Available — header + controls (only apply to this section) */}
            <Box mb={2}>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                    <WorkspacePremiumIcon sx={{ color: 'accent.main' }} />
                    <Typography variant="h6" fontWeight={600}>
                        All Available Badges
                    </Typography>
                    {total > 0 && (
                        <Typography variant="body2" color="text.secondary">
                            ({total})
                        </Typography>
                    )}
                </Box>
                <Box display="flex" alignItems="center" flexWrap="wrap" gap={1.5}>
                    <TextField
                        size="small"
                        placeholder="Search badges…"
                        value={search}
                        onChange={e => handleSearchChange(e.target.value)}
                        sx={{ minWidth: 240 }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <FormControl size="small" sx={{ minWidth: 180 }}>
                        <InputLabel id="badges-sort-label">Sort by</InputLabel>
                        <Select
                            labelId="badges-sort-label"
                            label="Sort by"
                            value={sort}
                            onChange={e => handleSortChange(e.target.value as SortField)}
                        >
                            <MenuItem value="createddate">Creation date</MenuItem>
                            <MenuItem value="verified">Verified issuer</MenuItem>
                            <MenuItem value="name">Name</MenuItem>
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 140 }}>
                        <InputLabel id="badges-order-label">Order</InputLabel>
                        <Select
                            labelId="badges-order-label"
                            label="Order"
                            value={order}
                            onChange={e => handleOrderChange(e.target.value as SortOrder)}
                        >
                            <MenuItem value="desc">Descending</MenuItem>
                            <MenuItem value="asc">Ascending</MenuItem>
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 180 }}>
                        <InputLabel id="badges-type-label">Badge type</InputLabel>
                        <Select
                            labelId="badges-type-label"
                            label="Badge type"
                            value={typeFilter}
                            onChange={e => handleTypeChange(e.target.value as TypeFilter)}
                        >
                            <MenuItem value="all">All types</MenuItem>
                            {BADGE_TYPES.map(t => (
                                <MenuItem key={t.type} value={t.type}>{t.label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Button color="secondary" onClick={handleClear}>Clear</Button>
                </Box>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {/* Grid */}
            {/* assignedLoading is in here for the same reason as the favourites panel.
                It only ever loads once, on mount, so paging and searching are unaffected. */}
            {loading || assignedLoading ? (
                <Grid container spacing={3}>
                    {Array.from({ length: PAGE_SIZE }).map((_, i) => (
                        <Grid item xs={12} sm={6} md={4} key={i}>
                            <SkeletonCard />
                        </Grid>
                    ))}
                </Grid>
            ) : badges.length === 0 ? (
                <Box textAlign="center" py={8} color="text.secondary">
                    <WorkspacePremiumIcon sx={{ fontSize: 64, opacity: 0.3 }} />
                    <Typography mt={2}>
                        {debouncedSearch || typeFilter !== 'all'
                            ? 'No badges match your filters.'
                            : 'No public badges available yet.'}
                    </Typography>
                    {(debouncedSearch || typeFilter !== 'all') && (
                        <Button color="secondary" onClick={handleClear} sx={{ mt: 1 }}>Clear filters</Button>
                    )}
                </Box>
            ) : (
                <Grid container spacing={3} data-testid="available-badges-grid">
                    {badges.map((badge) => (
                        <Grid item xs={12} sm={6} md={4} key={badge.badgeID}>
                            <BadgeCard
                                badge={badge}
                                showGoButton
                                isEarned={earnedIDs.has(badge.badgeID)}
                                isFavourite={favIDs.has(badge.badgeID)}
                                onToggleFavourite={handleToggleFavourite}
                            />
                        </Grid>
                    ))}
                </Grid>
            )}

            {/* Pagination */}
            {!loading && totalPages > 1 && (
                <Box display="flex" justifyContent="center" mt={4}>
                    <Pagination
                        count={totalPages}
                        page={page}
                        onChange={(_, p) => setPage(p)}
                        color="primary"
                    />
                </Box>
            )}
        </Container>
    );
}
